import { useParams, useLocation } from 'react-router-dom';
import { Snackbar, Alert, useTheme, useMediaQuery } from '@mui/material';
import BasePage from './BasePage';
import { useState } from 'react';
import { SnackbarState } from '../types/homes';
import { ChatProvider } from '../contexts/ChatContext';
import { HomeAttachmentsProvider } from '../contexts/HomeAttachmentsContext';
import { HomeDetailsProvider } from '../contexts/HomeDetailsContext';
import { HomeAreasProvider } from '../contexts/HomeAreasContext';
import { HomeRepairsProvider } from '../contexts/HomeRepairsContext';
import HomeDetailsWithIntelligence from '../components/HomeDetailsWithIntelligence';
import HomeDetailsDesktop from './HomeDetailsDesktop';
import HomeDetailsMobile from './HomeDetailsMobile';
import { analytics } from '../services/analytics';
import { useAnalytics } from '../hooks/useAnalytics';
import { useAuth } from '../contexts/AuthContext';

function HomeDetails() {
  const { homeId } = useParams<{ homeId: string }>();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  
  const isDetailView = !location.pathname.includes('/chat') && 
                       !location.pathname.includes('/attachments') && 
                       !location.pathname.includes('/areas');

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Log home_opened analytics event
  useAnalytics(
    () => {
      if (user?.uuid && homeId) {
        analytics.logHomeOpened(user.uuid, homeId);
      }
    },
    [homeId, user?.uuid]
  );

  const handleMessage = (message: string) => {
    setSnackbar({
      open: true,
      message,
      severity: 'success'
    });
  };

  const handleError = (error: string) => {
    setSnackbar({
      open: true,
      message: error,
      severity: 'error'
    });
  };
  
  return (
    <BasePage
      title="CribCritic Home Details"
      description="Detailed view of home evaluations and insights."
      keywords="home details, property insights, home evaluation"
      canonicalUrl={`https://cribcritic.ai/home/${homeId}`}
    >
      <HomeDetailsProvider homeId={homeId ?? ''}>
        <HomeDetailsWithIntelligence>
          <HomeAreasProvider
            homeId={homeId ?? ''}
            onSuccess={handleMessage}
            onError={handleError}
          >
            <ChatProvider homeId={homeId ?? ''}>
              <HomeAttachmentsProvider
                homeId={homeId ?? ''}
                onSuccess={handleMessage}
                onError={handleError}
              >
                <HomeRepairsProvider
                  homeId={homeId ?? ''}
                  onSuccess={handleMessage}
                  onError={handleError}
                >
                  {isMobile ? (
                    <HomeDetailsMobile isDetailView={isDetailView} />
                  ) : (
                    <HomeDetailsDesktop isDetailView={isDetailView} />
                  )}
                </HomeRepairsProvider>
              </HomeAttachmentsProvider>
            </ChatProvider>
          </HomeAreasProvider>
        </HomeDetailsWithIntelligence>
      </HomeDetailsProvider>
      
      <Snackbar 
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </BasePage>
  );
}

export default HomeDetails;